import * as React from 'react';
import type { WrappedFieldProps } from 'redux-form';

import { TextInput, NumberInput, PhoneInput } from '@acceligentllc/storybook';

import type { OwnProps as TooltipProps } from 'af-components/Tooltip';

export type Sizes = 'sm' | 'lg';

interface Props extends WrappedFieldProps {
	type: 'text' | 'number' | 'hidden' | 'password' | 'email' | 'tel';
	name: string;
	label?: string;
	isRequired?: boolean;
	placeholder: string;
	disabled?: boolean;
	onValueChange: (value: string) => Promise<void>;
	min?: number;
	max?: number;
	maxCharacters?: number;
	showCharNum?: boolean;
	addonAfter?: React.ReactNode;
	iconRight?: string;
	hideErrorText?: boolean;
	inputSize?: Sizes;
	forceShowError?: boolean;
	tooltipMessage?: TooltipProps['message'];
	autoFocus?: boolean;
	onKeyDown?: (event: Metadata) => void;
	onFocus?: (event: Metadata) => void;
	onBlur?: (event: Metadata) => void;
	disableErrors?: boolean;
	isStandalone?: boolean;
	additionalActionIcon?: string;
	additionalAction?: () => void;
	iconLeft?: string;
	hasClear?: boolean;

	// number-specific props

	/** Only on type="number" */
	withThousandsSeparator?: boolean;
	/** Only on type="number" */
	isZeroIfEmpty?: boolean;
	/** Only on type="number". In component, defaults to true, so typically you can use negative numbers */
	allowNegative?: boolean;
	/** Only on type="number". Caps the digits before the decimal point. Keystrokes past the cap are rejected. */
	maxIntegerDigits?: number;
	/** Only on type="number". Decimal places accepted. Set to 2 by default */
	decimalPoints?: number;
}

export type InputProps = Props;

export const SIZE_MAP: Record<Sizes, 'small' | 'big'> = {
	sm: 'small',
	lg: 'big',
};

class Input extends React.Component<Props> {
	static defaultProps: Partial<Props> = {
		showCharNum: false,
		hideErrorText: false,
		disableErrors: false,
		forceShowError: false,
		isStandalone: false,
		maxCharacters: 750,
	};

	/**
	 * @param propName Usually a form prop name, such as form.item[0].attribute
	 * @returns Hopefully unique ID for QA team to attach tests to
	 */
	static _generateQAId = (propName: string) => `accqa__${propName.replaceAll('[', '-').replaceAll('.', '__').replaceAll(']', '')}`;

	onValChange = async (value: string) => {
		const { onValueChange, input } = this.props;

		if (input.onChange) {
			input.onChange(value);
		}
		if (onValueChange) {
			onValueChange(value);
		}
	};

	onFocus = (event) => {
		const { input, onFocus } = this.props;

		input.onFocus(event);

		if (onFocus) {
			onFocus(event);
		}
	};

	onBlur = (event) => {
		const { input, onBlur } = this.props;

		input.onBlur(event);

		if (onBlur) {
			onBlur(event);
		}
	};

	render() {
		const {
			disabled,
			input,
			label,
			isRequired,
			type,
			hideErrorText,
			meta: { touched, error, warning },
			min,
			max,
			maxCharacters,
			addonAfter,
			iconRight,
			inputSize,
			forceShowError,
			tooltipMessage,
			autoFocus,
			onKeyDown,
			disableErrors,
			isStandalone,
			placeholder,
			additionalAction,
			additionalActionIcon,
			hasClear,
			withThousandsSeparator,
			isZeroIfEmpty,
			allowNegative,
			maxIntegerDigits,
			decimalPoints,
		} = this.props;

		// redux-form hands over { name, value, checked, onChange, onFocus, onBlur, onDragStart, onDrop }.
		// The three handlers below are wrapped, and `checked` only applies to checkbox/radio inputs.
		// Everything else is forwarded as-is so nothing gets dropped by being left off a list —
		// notably onDragStart/onDrop, which keep the store in sync when text is dragged into or
		// out of the field, an interaction that never fires onChange.

		const hasError = !!(touched && !disableErrors && error);

		const allowErrorMessage = !!(!disableErrors && !hideErrorText && (touched || forceShowError)) && !!error;
		const allowWarningMessage = !!(!disableErrors && !hideErrorText && (touched || forceShowError)) && !!warning;

		if (type === 'number') {
			// Splitting fields and how they are rendered
			return (
				<div className={`form-group ${isStandalone ? 'form-group--standalone' : ''}`.trim()}>
					<NumberInput
						{...input}
						additionalAction={additionalAction}
						additionalActionIcon={additionalActionIcon}
						allowNegative={allowNegative}
						autoFocus={autoFocus}
						decimalPoints={decimalPoints}
						disabled={disabled}
						error={allowErrorMessage ? error : undefined}
						hasClear={hasClear}
						hasError={hasError}
						isRequired={isRequired}
						isZeroIfEmpty={isZeroIfEmpty}
						label={label}
						max={max}
						maxIntegerDigits={maxIntegerDigits}
						min={min}
						onBlur={this.onBlur}
						onChange={this.onValChange}
						onFocus={this.onFocus}
						onKeyDown={onKeyDown}
						placeholder={placeholder}
						size={inputSize ? SIZE_MAP[inputSize] : 'regular'}
						tooltip={tooltipMessage}
						unit={addonAfter as string}
						value={input.value}
						warning={allowWarningMessage ? warning : undefined}
						withThousandsSeparator={withThousandsSeparator}
					/>
				</div>
			);
		}

		if (type === 'tel') {
			// Splitting fields and how they are rendered
			return (
				<div className={`form-group ${isStandalone ? 'form-group--standalone' : ''}`.trim()}>
					<PhoneInput
						{...input}
						additionalAction={additionalAction}
						additionalActionIcon={additionalActionIcon}
						autoFocus={autoFocus}
						disabled={disabled}
						error={allowErrorMessage ? error : undefined}
						hasClear={hasClear}
						hasError={hasError}
						isRequired={isRequired}
						label={label}
						onBlur={this.onBlur}
						onChange={this.onValChange}
						onFocus={this.onFocus}
						onKeyDown={onKeyDown}
						placeholder={placeholder}
						size={inputSize ? SIZE_MAP[inputSize] : 'regular'}
						tooltip={tooltipMessage}
						value={input.value}
						warning={allowWarningMessage ? warning : undefined}
					/>
				</div>
			);
		}

		return (
			<div className={`form-group ${isStandalone ? 'form-group--standalone' : ''}`.trim()}>
				<TextInput
					{...input}
					additionalAction={additionalAction}
					additionalActionIcon={additionalActionIcon}
					autoFocus={autoFocus}
					disabled={disabled}
					error={allowErrorMessage ? error : undefined}
					hasClear={hasClear}
					hasError={hasError}
					iconRight={iconRight}
					isRequired={isRequired}
					label={label}
					maxCharacters={maxCharacters}
					onBlur={this.onBlur}
					onChange={this.onValChange}
					onFocus={this.onFocus}
					onKeyDown={onKeyDown}
					placeholder={placeholder}
					size={inputSize ? SIZE_MAP[inputSize] : 'regular'}
					tooltip={tooltipMessage}
					type={type}
					value={input.value}
					warning={allowWarningMessage ? warning : undefined}
				/>
			</div>
		);
	}
}

export default Input;
